import type { Logger, LogsEvent } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import { env } from 'next-runtime-env';

import type { ILogger } from '@/logger/logger';
import { ConsoleLogger } from '@/logger/logger';

// Third-party analytics/tracker hosts that privacy browsers and ad-blockers
// (DuckDuckGo, Brave, uBlock, etc.) routinely block. The resulting failed
// requests are auto-forwarded by `forwardErrorsToLogs` as error logs even
// though they're expected, out of our control, and not actionable — so we drop
// them below to keep the error stream signal-heavy.
const THIRD_PARTY_TRACKER_HOSTS = ['api.segment.io', 'cdn.segment.com'];

function isBlockedTrackerNoise(event: LogsEvent): boolean {
  if (event.origin !== 'network') {
    return false;
  }
  const url = event.http?.url ?? event.message ?? '';
  return THIRD_PARTY_TRACKER_HOSTS.some((host) => url.includes(host));
}

export class BrowserLogger implements ILogger {
  private readonly ddLogger: Logger;

  constructor(clientToken: string, environment: string) {
    const loggerName = 'ecom-order-portal';
    // Only init once per page to avoid "SDK is loaded more than once" (e.g. Next.js
    // can evaluate this module in multiple chunks or after HMR).
    if (!datadogLogs.getInitConfiguration()) {
      datadogLogs.init({
        clientToken: clientToken,
        env: environment,
        service: loggerName,
        site: 'datadoghq.com',
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        beforeSend: (event) => !isBlockedTrackerNoise(event),
      });
    }
    this.ddLogger =
      datadogLogs.getLogger(loggerName) ?? datadogLogs.createLogger(loggerName);
  }

  public debug(message: string, options?: unknown): void {
    this.ddLogger.debug(message, options || {});
  }

  public info(message: string, options?: unknown): void {
    this.ddLogger.info(message, options || {});
  }

  public warn(message: string, options?: unknown): void {
    this.ddLogger.warn(message, options || {});
  }

  public error(message: string, options?: unknown): void {
    this.ddLogger.error(message, options || {});
  }
}

let iLogger: ILogger | undefined;

function getBrowserLogger() {
  if (iLogger) {
    return iLogger;
  }

  const clientToken = env('NEXT_PUBLIC_DD_CLIENT_TOKEN');
  const environment = env('NEXT_PUBLIC_OURA_ENV') ?? 'development';
  if (clientToken && environment !== 'development') {
    iLogger = new BrowserLogger(clientToken, environment);
  } else {
    iLogger = new ConsoleLogger();
  }
  return iLogger;
}

export const logger = getBrowserLogger();
